// DATAMODEL-01: facturacion pasa de long a enum por tramos. Lo que
// queda aquí son los slugs canónicos (FACTURACION_RANGES) para
// validaciones puntuales (p.ej. comprobar que un valor URL está en
// la lista) y los rangos de empleados que sigue siendo long.

// Nota: extensión .js explícita — este módulo lo carga también Node en
// crudo (scripts/migrate-empleados-idx.js vía import() dinámico), y el
// resolutor ESM de Node no completa extensiones. Webpack y jest lo
// resuelven igual con extensión.
import { EMPLEADOS_SCALE, ANTIGUEDAD_SCALE } from './configListing.js';

// Slugs canónicos del enum `facturacion` (orden ascendente). Los
// labels viven en configListing.js → enumOptions (mismo orden: es la
// fuente del índice de ordenación, ver facturacionIdxForOption).
export const FACTURACION_RANGES = [
  '0-250k',
  '250-500k',
  '500-1000k',
  '1000-1500k',
  '1500-2000k',
  '2000-3000k',
  '3000-5000k',
  '5000-10000k',
  '10000-25000k',
  'mas-25000k',
];

// FACTURACION-IDX-01 (3b): índice ordinal 1..N del tramo, derivado de
// la posición en FACTURACION_RANGES (= orden de enumOptions en
// configListing.js). Se persiste como pub_facturacionIdx (long) para
// poder filtrar por rango y ordenar — el enum no es ordenable en la
// Marketplace API. null si el slug no es válido.
export const facturacionIdxForOption = option => {
  const i = FACTURACION_RANGES.indexOf(option);
  return i === -1 ? null : i + 1;
};

// Límites de cada tramo en miles de € (parseados del slug K-based).
// upperK es null en el tramo abierto ('mas-25000k').
export const facturacionTramoBounds = () =>
  FACTURACION_RANGES.map((slug, i) => {
    const m = slug.match(/^(\d+)-(\d+)k$/);
    const open = slug.match(/^mas-(\d+)k$/);
    return m
      ? { idx: i + 1, lowerK: Number(m[1]), upperK: Number(m[2]) }
      : { idx: i + 1, lowerK: Number(open[1]), upperK: null };
  });

// Formatea un límite en miles de € para los selects Desde–Hasta:
// 0 → "0 €", 250 → "250.000 €", 1000 → "1 M€", 1500 → "1,5 M€".
export const formatFacturacionLimitK = k => {
  if (k < 1000) return k === 0 ? '0 €' : `${k}.000 €`;
  const millions = k / 1000;
  const formatted = Number.isInteger(millions)
    ? `${millions}`
    : `${millions}`.replace('.', ',');
  return `${formatted} M€`;
};

// ─── Antigüedad ────────────────────────────────────────────────────
// FASE2-SCHEMA-01.3: mismo patrón que facturacion → facturacionIdx.
// `antiguedad` sigue siendo el enum visible (card y ficha); el ordinal
// se persiste aparte como pub_antiguedadIdx (long) porque el enum no se
// puede cruzar por rango con el intervalo del mandato
// (antiguedadIdxMin/Max), que es justo lo que hace el matching.

// Tramos parseados: { option, label, lowerBound, upperBound, idx }.
export const antiguedadTramos = () => ANTIGUEDAD_SCALE.map(t => ({ ...t }));

// Ordinal 1..N del tramo cuyo slug es `option`. ESTRICTO: null si el
// slug no está en la escala. El enum del wizard es cerrado, así que un
// slug desconocido solo puede venir de un dato corrupto o de una opción
// retirada; es preferible dejar el índice vacío a inventar un tramo.
// Lo usan el submit del wizard y la migración: aquí no hay contención
// que resolver (a diferencia de empleados, que llegó a tener valores
// libres), porque `antiguedad` ha sido siempre un enum cerrado.
export const antiguedadIdxForOption = option => {
  const found = ANTIGUEDAD_SCALE.find(t => t.option === option);
  return found ? found.idx : null;
};

// FILTERS-03: Nº empleados — UI de dropdown único. El valor emitido
// es un rango "min,max" compatible con el filtro long del API.
// DATAMODEL-01: 5 tramos exactos definidos por Uroborox (sin "Sin empleados").
// FASE2-SCHEMA-01.2: derivado de EMPLEADOS_SCALE (configListing.js), que
// pasa a ser la fuente única de tramos, labels y ordinales.
export const EMPLEADOS_OPTIONS = EMPLEADOS_SCALE.map(({ lowerBound, upperBound, label }) => ({
  value: `${lowerBound},${upperBound}`,
  label,
}));

// EMPLEADOS-TRAMOS-01: helpers para el select de tramos del wizard.
// pub_empleados sigue siendo long: se persiste el límite inferior del
// tramo (1/6/16/51/101), que es el valor visible en card y ficha.

// Tramos parseados: { lowerBound, upperBound, label, idx }.
export const empleadosTramos = () =>
  EMPLEADOS_SCALE.map(({ lowerBound, upperBound, label, idx }) => ({
    lowerBound,
    upperBound,
    label,
    idx,
  }));

// FASE2-SCHEMA-01.2b: tramo de EMPLEADOS_SCALE que CONTIENE `count`.
// Es el criterio de la migración (scripts/migrate-empleados-idx.js): los
// anuncios anteriores a 0363f8452 guardan valores libres (75, 30, 8…)
// que no son límites de tramo, y cada uno pertenece sin ambigüedad al
// tramo que lo contiene. Normalizarlos al límite inferior de ESE tramo
// no cambia el label que ve el usuario.
//
// El último tramo es abierto por arriba (101 o más → último), igual que
// "Más de 25 M€" en facturación. Devuelve null solo para valores
// genuinamente inválidos: ausentes, no numéricos o menores que el
// límite inferior del primer tramo.
export const empleadosTramoForCount = count => {
  const n = Number(count);
  if (count == null || count === '' || Number.isNaN(n)) return null;
  if (n < EMPLEADOS_SCALE[0].lowerBound) return null;
  return (
    EMPLEADOS_SCALE.find(t => n <= t.upperBound) || EMPLEADOS_SCALE[EMPLEADOS_SCALE.length - 1]
  );
};

// FASE2-SCHEMA-01.2: ordinal 1..N del tramo cuyo límite inferior es
// `lowerBound`. ESTRICTO a propósito: devuelve null si el valor no es
// exactamente un límite de tramo. Lo usa el submit del wizard, cuyo
// select cerrado solo puede emitir límites: si llegara otra cosa sería
// un bug, y es preferible limpiar el índice a inventar un tramo.
// La migración NO lo usa — ahí el criterio es la contención de arriba.
export const empleadosIdxForLowerBound = lowerBound => {
  const n = Number(lowerBound);
  if (lowerBound == null || Number.isNaN(n)) return null;
  const found = EMPLEADOS_SCALE.find(t => t.lowerBound === n);
  return found ? found.idx : null;
};

// Límite inferior del tramo que contiene `count`. Para valores fuera de
// rango (datos legacy: 0, >200) se devuelve el tramo más cercano. null
// si count no es un número.
export const empleadosLowerBoundForCount = count => {
  const n = Number(count);
  if (count == null || Number.isNaN(n)) return null;
  const tramos = empleadosTramos();
  const found = tramos.find(t => n >= t.lowerBound && n <= t.upperBound);
  if (found) return found.lowerBound;
  return n < tramos[0].lowerBound ? tramos[0].lowerBound : tramos[tramos.length - 1].lowerBound;
};
